import { Parameter } from "domain/entities/parameter";
import { Salary } from "domain/entities/salary";
import { SalaryInputFilter } from "domain/models/salary-input-filter";
import { translateText } from "infra/translation/translate";
import { useSalariesByFilter } from "presentation/hooks/use-query-salary";
import { useDependentsByFilter } from "presentation/hooks/use-query-dependent";
import { useEmployeeFieldsStore } from "presentation/stores/use-employee-fields-store";
import { useEmployeeStore } from "presentation/stores/use-employee-store";
import DropdownComponent from "presentation/components/Shared/DropdownComponent";
import InputCurrencyComponent from "presentation/components/Shared/InputCurrencyComponent";
import { MandatorySignal } from "presentation/components/Shared/MandatorySignalComponent";
import React, { useEffect, useState } from "react";
import InputNumberComponent from "presentation/components/Shared/InputNumberComponent";
import { Tooltip } from "primereact/tooltip";

interface SalaryFormProps {
  canEdit: boolean;
  employeeId?: string;
}

export const SalaryForm: React.FC<SalaryFormProps> = ({
  canEdit,
  employeeId,
}) => {
  const { employee, setEmployee } = useEmployeeStore();
  const { employeeFields } = useEmployeeFieldsStore();

  const getInitialSalary = (): Salary => {
    return (
      employee?.salary ||
      ({
        grossBaseValue: 0,
        foodAllowanceValue: 0,
        costsAllowanceValue: 0,
        unaBonusValue: 0,
        educationCheckTotalValue: 0,
        childhoodCheckTotalValue: 0,
        trainingValue: 0,
        ihtValue: 0,
        dailyValue: 0,
        supportPreventionValue: 0,
      } as Salary)
    );
  };

  const [salary, setSalary] = useState<Salary>(getInitialSalary());

  const [hasPaidVacation, setHasPaidVacation] = useState<boolean>(false);
  const [paidVacationDays, setPaidVacationDays] = useState<number>(0);

  const { data: salaryData } = useSalariesByFilter({
    employeeId: employeeId || employee?.id,
  } as SalaryInputFilter);

  const { data: dependentsData } = useDependentsByFilter({
    employeeId: employeeId || employee?.id,
  });

  // Variáveis calculadas
  // Verifica se tem carta de condução
  const hasDriverLicense = employee?.hasDrivingLicence === true;
  const hasChildren = (dependentsData?.data?.length || 0) > 0;

  useEffect(() => {
    const salaryFromApi = salaryData?.data?.at(0);
    if (salaryFromApi) {
      // Garantir que valores null/undefined sejam convertidos para 0
      const normalizedSalary = {
        ...salaryFromApi,
        unaBonusValue: salaryFromApi.unaBonusValue ?? 0,
        ihtValue: salaryFromApi.ihtValue ?? 0,
      };
      setSalary(normalizedSalary);
      setHasPaidVacation(salaryFromApi.hasPaidVacation || false);
      setPaidVacationDays(salaryFromApi.paidVacationDays || 0);
    }
  }, [salaryData]);

  useEffect(() => {
    const updatedSalary = { ...salary, hasPaidVacation, paidVacationDays };
    // Não incluir employee nas dependências para evitar loop infinito
    setEmployee({ salary: updatedSalary });
  }, [salary, hasPaidVacation, paidVacationDays, setEmployee]);

  // Reset IHT quando Base Bruto <= 870€
  useEffect(() => {
    if (salary?.grossBaseValue && salary.grossBaseValue <= 870 && salary?.ihtValue && salary.ihtValue > 0) {
      setSalary((prev) => ({ ...prev, ihtValue: 0 } as Salary));
    }
  }, [salary?.grossBaseValue, salary?.ihtValue]);

  // Calcular valores dos cheques automaticamente baseado nos dependentes
  useEffect(() => {
    if (dependentsData?.data && dependentsData.data.length > 0) {
      const today = new Date();

      let totalChildhood = 0;
      let totalEducation = 0;
      
      dependentsData.data.forEach((dependent) => {
        if (dependent.birthDate) {
          const birthDate = new Date(dependent.birthDate);
          let age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          }

          if (age < 7) {
            totalChildhood += dependent.childhoodCheckValue || 0;
          } else {
            totalEducation += dependent.educationCheckValue || 0;
          }
        }
      });

      // Atualizar apenas se os valores mudaram
      if (salary.childhoodCheckTotalValue !== totalChildhood ||
        salary.educationCheckTotalValue !== totalEducation) {
        setSalary((prev) => ({
          ...prev,
          childhoodCheckTotalValue: totalChildhood,
          educationCheckTotalValue: totalEducation,
        } as Salary));
      }
    } else if (salary.childhoodCheckTotalValue !== 0 || salary.educationCheckTotalValue !== 0) {
      // Se não há dependentes, zerar os valores
      setSalary((prev) => ({
        ...prev,
        childhoodCheckTotalValue: 0,
        educationCheckTotalValue: 0,
      } as Salary));
    }
  }, [dependentsData, salary.childhoodCheckTotalValue, salary.educationCheckTotalValue]);

  const irsTypeOptions: Parameter[] = [
    { id: "IRS_JOVEM", value: "IRS Jovem", type: "irsType" },
    { id: "IRS_NORMAL", value: "IRS Normal", type: "irsType" },
  ];

  const booleanOptions: Parameter[] = [
    { id: "true", value: "Sim", type: "boolean" },
    { id: "false", value: "Não", type: "boolean" },
  ]

  const replaceArrayBoolean = [
    { from: "Sim", to: true },
    { from: "Não", to: false },
  ];

  const isCTIorCTP =
    employee?.contractType === "CTI" || employee?.contractType === "CTP";
  const isCPS =
    employee?.contractType === "CPS Nacional" ||
    employee?.contractType === "CPS Internacional";
  const isCPSNacional = employee?.contractType === "CPS Nacional";

  return (
    <div>
      <div className="default-grid">
        {isCTIorCTP && (
          <div className="default-1-col">
            <InputCurrencyComponent
              label={`${translateText(
                "Gross Base Value",
                "pt"
              )} ${MandatorySignal()}`}
              fieldName="grossBaseValue"
              currentObject={salary}
              getValue={salary?.grossBaseValue}
              setValue={setSalary}
              isInvalid={
                employeeFields.find((item) => item.field === "grossBaseValue")
                  ?.invalid
              }
              isDisable={!canEdit}
            />
          </div>
        )}

        {isCTIorCTP && (
          <div className="default-1-col">
            <div style={{ position: "relative" }}>
              <InputCurrencyComponent
                label={`${translateText(
                  "Food Allowance Value",
                  "pt"
                )} ${MandatorySignal()}`}
                fieldName="foodAllowanceValue"
                currentObject={salary}
                getValue={salary?.foodAllowanceValue}
                setValue={setSalary}
                isInvalid={
                  employeeFields.find(
                    (item) => item.field === "foodAllowanceValue"
                  )?.invalid
                }
                isDisable={!canEdit}
              />
              <i
                className="pi pi-info-circle food-allowance-info"
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: "1.1rem",
                  color: "#3b82f6",
                  cursor: "help",
                  zIndex: 10,
                }}
              />
              <Tooltip
                target=".food-allowance-info"
                position="right"
                style={{ maxWidth: "300px" }}
              >
                <div style={{ lineHeight: "1.6" }}>
                  <strong>{translateText("Food Allowance", "pt")}</strong>
                  <br />• {translateText("Paid via meal card", "pt")}
                </div>
              </Tooltip>
            </div>
          </div>
        )}

        {isCTIorCTP && (
          <div className="default-1-col">
            <div style={{ position: "relative" }}>
              <InputCurrencyComponent
                label={translateText("Costs Allowance Value", "pt")}
                fieldName="costsAllowanceValue"
                currentObject={salary}
                getValue={salary?.costsAllowanceValue}
                setValue={setSalary}
                isInvalid={
                  employeeFields.find(
                    (item) => item.field === "costsAllowanceValue"
                  )?.invalid
                }
                isDisable={!canEdit || !hasDriverLicense}
              />
              <i
                className={`pi pi-info-circle costs-allowance-info`}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: "1.1rem",
                  color: "#3b82f6",
                  cursor: "help",
                  zIndex: 10,
                }}
              />
              <Tooltip
                target=".costs-allowance-info"
                position="right"
                style={{ maxWidth: "300px" }}
              >
                <div style={{ lineHeight: "1.6" }}>
                  <strong>{translateText("Cost Allowance", "pt")}</strong>
                  <br />• {translateText("Requires driving license", "pt")}
                </div>
            
              </Tooltip>
            </div>
          </div>
        )}

        {isCTIorCTP && (
          <div className="default-1-col">
            <InputCurrencyComponent
              label={translateText("UNA Bonus Value", "pt")}
              fieldName="unaBonusValue"
              currentObject={salary}
              getValue={salary?.unaBonusValue}
              setValue={setSalary}
              isInvalid={
                employeeFields.find((item) => item.field === "unaBonusValue")
                  ?.invalid
              }
              isDisable={!canEdit}
            />
          </div>
        )}

        {isCTIorCTP && (
          <div className="default-1-col">
            <div style={{ position: "relative" }}>
              <InputCurrencyComponent
                label={translateText("Childhood Check Total Value", "pt")}
                fieldName="childhoodCheckTotalValue"
                currentObject={salary}
                getValue={salary?.childhoodCheckTotalValue}
                setValue={setSalary}
                isInvalid={
                  employeeFields.find(
                    (item) => item.field === "childhoodCheckTotalValue"
                  )?.invalid
                }
                isDisable={true}
              />
              <>
                <i
                  className={`pi ${hasChildren ? 'pi-info-circle' : 'pi-exclamation-triangle'} childhood-check-info`}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: "1.1rem",
                    color: hasChildren ? "#3b82f6" : "#f59e0b",
                    cursor: "help",
                    zIndex: 10,
                  }}
                />
                <Tooltip
                  target=".childhood-check-info"
                  position="right"
                  style={{ maxWidth: "300px" }}
                >
                  {hasChildren ? (
                    <div style={{ lineHeight: "1.6" }}>
                      <strong>{translateText("For children under 7 years", "pt")}</strong>
                      <br />• {translateText("Not editable here", "pt")}
                      <br />• {translateText("Values set in Dependents tab", "pt")}
                      <br />• {translateText("Migrates to Education Check at 7 years", "pt")}
                    </div>
                  ) : (
                    <div style={{ lineHeight: "1.6" }}>
                      <strong>⚠️ {translateText("Requires registered children", "pt")}</strong>
                      <br />• {translateText("Add dependents in Dependents tab", "pt")}
                    </div>
                  )}
                </Tooltip>
              </>
            </div>
          </div>
        )}

        {isCTIorCTP && (
          <div className="default-1-col">
            <div style={{ position: "relative" }}>
              <InputCurrencyComponent
                label={translateText("Education Check Total Value", "pt")}
                fieldName="educationCheckTotalValue"
                currentObject={salary}
                getValue={salary?.educationCheckTotalValue}
                setValue={setSalary}
                isInvalid={
                  employeeFields.find(
                    (item) => item.field === "educationCheckTotalValue"
                  )?.invalid
                }
                isDisable={true}
              />
              <>
                <i
                  className={`pi ${hasChildren ? 'pi-info-circle' : 'pi-exclamation-triangle'} education-check-info`}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: "1.1rem",
                    color: hasChildren ? "#3b82f6" : "#f59e0b",
                    cursor: "help",
                    zIndex: 10,
                  }}
                />
                <Tooltip
                  target=".education-check-info"
                  position="right"
                  style={{ maxWidth: "300px" }}
                >
                  {hasChildren ? (
                    <div style={{ lineHeight: "1.6" }}>
                      <strong>{translateText("For children 7 years or older", "pt")}</strong>
                      <br />• {translateText("Not editable here", "pt")}
                      <br />• {translateText("Values set in Dependents tab", "pt")}
                      <br />• {translateText("Review annually plus monthly adjustments", "pt")}
                    </div>
                  ) : (
                    <div style={{ lineHeight: "1.6" }}>
                      <strong>⚠️ {translateText("Requires registered children", "pt")}</strong>
                      <br />• {translateText("Add dependents in Dependents tab", "pt")}
                    </div>
                  )}
                </Tooltip>
              </>
            </div>
          </div>
        )}

        {isCTIorCTP && (
          <div className="default-1-col">
            <div style={{ position: "relative" }}>
              <InputCurrencyComponent
                label={translateText("Training Value", "pt")}
                fieldName="trainingValue"
                currentObject={salary}
                getValue={salary?.trainingValue}
                setValue={setSalary}
                isInvalid={
                  employeeFields.find((item) => item.field === "trainingValue")
                    ?.invalid
                }
                isDisable={!canEdit}
              />
              <i
                className="pi pi-info-circle training-info"
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: "1.1rem",
                  color: "#3b82f6",
                  cursor: "help",
                  zIndex: 10,
                }}
              />
              <Tooltip
                target=".training-info"
                position="right"
                style={{ maxWidth: "350px" }}
              >
                <div style={{ lineHeight: "1.6" }}>
                  <strong>{translateText("Training", "pt")}</strong>
                  <br />• {translateText("Training must be approved by Nimber who will also pay and send access", "pt")}
                  <br />• {translateText("This value is not paid to the employee", "pt")}
                </div>
              </Tooltip>
            </div>
          </div>
        )}

        {isCTIorCTP && (
          <div className="default-1-col">
            <div style={{ position: "relative" }}>
              <InputCurrencyComponent
                label={translateText("IHT Value", "pt")}
                fieldName="ihtValue"
                currentObject={salary}
                getValue={salary?.ihtValue}
                setValue={setSalary}
                isInvalid={
                  employeeFields.find((item) => item.field === "ihtValue")
                    ?.invalid
                }
                isDisable={!canEdit || (salary?.grossBaseValue ?? 0) <= 870}
              />
              <>
                <i
                  className="pi pi-info-circle iht-info"
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: "1.1rem",
                    color: "#3b82f6",
                    cursor: "help",
                    zIndex: 10,
                  }}
                />
                <Tooltip
                  target=".iht-info"
                  position="left"
                  style={{ maxWidth: "300px" }}
                >
                  <div style={{ lineHeight: "1.6" }}>
                    <strong>{translateText("IHT (Housing and Work Tax)", "pt")}</strong>
                    <br />• {translateText("Only applicable if Gross Base exceeds 870€", "pt")}
                  </div>
                </Tooltip>
              </>
            </div>
          </div>
        )}

        {isCTIorCTP && (
          <div className="default-1-col">
            <div style={{ position: "relative" }}>
              <DropdownComponent
                options={irsTypeOptions}
                label={`${translateText("IRS Type", "pt")} ${MandatorySignal()}`}
                fieldName="irsType"
                currentObject={salary}
                getValue={salary?.irsType}
                setValue={setSalary}
                isInvalid={
                  employeeFields.find((item) => item.field === "irsType")?.invalid
                }
                isDisable={!canEdit}
              />
              {salary?.irsType === "IRS Jovem" && (
                <>
                  <i
                    className="pi pi-info-circle iht-info"
                    style={{
                      position: "absolute",
                      right: "45px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      color: "#3b82f6",
                      fontSize: "1rem",
                    }}
                    data-pr-tooltip={translateText("Review annually (Dec/Jan) to confirm eligibility and update percentage", "pt")}
                    data-pr-position="top"
                  />
                  <Tooltip target=".warning-icon" />
                </>
              )}
            </div>
          </div>
        )}

        {isCPS && (
          <div className="default-1-col">
            <div style={{ position: "relative" }}>
              <InputCurrencyComponent
                label={`${translateText(
                  "Daily Value",
                  "pt"
                )} ${MandatorySignal()}`}
                fieldName="dailyValue"
                currentObject={salary}
                getValue={salary?.dailyValue}
                setValue={setSalary}
                isInvalid={
                  employeeFields.find((item) => item.field === "dailyValue")
                    ?.invalid
                }
                isDisable={!canEdit}
              />
              {isCPSNacional && (
                <>
                  <i
                    className="pi pi-info-circle daily-value-info"
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: "1.1rem",
                      color: "#3b82f6",
                      cursor: "help",
                      zIndex: 10,
                    }}
                  />
                  <Tooltip
                    target=".daily-value-info"
                    position="right"
                    style={{ maxWidth: "300px" }}
                  >
                    <div style={{ lineHeight: "1.6" }}>
                      <strong>{translateText("Automatic VAT", "pt")}</strong>
                      <br />• {translateText("VAT calculated automatically", "pt")}
                      <br />• {translateText("Fixed legal percentage applied on daily value", "pt")}
                    </div>
                  </Tooltip>
                </>
              )}
            </div>
          </div>
        )}

        {isCPS && (
          <div className="default-1-col">
            <DropdownComponent
              options={booleanOptions}
              replaceArray={replaceArrayBoolean}
              label={translateText("Paid vacation", "pt")}
              fieldName="hasPaidVacation"
              currentObject={{ hasPaidVacation }}
              getValue={hasPaidVacation ? "Sim" : "Não"}
              setValue={(obj: any) => {
                const value = typeof obj === 'object' ? obj.hasPaidVacation : obj;
                setHasPaidVacation(value);
                if (!value) setPaidVacationDays(0);
              }}
              isDisable={!canEdit}
            />
          </div>
        )}

        {isCPS && hasPaidVacation && (
          <div className="default-1-col">
            <InputNumberComponent
              label={`${translateText(
                "Paid vacation days",
                "pt"
              )} ${MandatorySignal()}`}
              fieldName="paidVacationDays"
              currentObject={{ paidVacationDays }}
              getValue={paidVacationDays}
              setValue={(obj: any) => {
                const days = obj.paidVacationDays ?? 0;
                setPaidVacationDays(days);
                if (days === 0) {
                  setHasPaidVacation(false);
                }
              }}
              isDisable={!canEdit}
            />
          </div>
        )}

        {isCTIorCTP && (
          <div className="default-1-col">
            <div style={{ position: "relative" }}>
              <InputCurrencyComponent
                label={translateText("Support Prevention Value", "pt")}
                fieldName="supportPreventionValue"
                currentObject={salary}
                getValue={salary?.supportPreventionValue}
                setValue={setSalary}
                isInvalid={
                  employeeFields.find(
                    (item) => item.field === "supportPreventionValue"
                  )?.invalid
                }
                isDisable={!canEdit || !employee?.isSuportService}
              />
              {!employee?.isSuportService && (
                <>
                  <i
                    className="pi pi-info-circle support-prevention-info"
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: "1.1rem",
                      color: "#3b82f6",
                      cursor: "help",
                      zIndex: 10,
                    }}
                  />
                  <Tooltip
                    target=".support-prevention-info"
                    position="right"
                    style={{ maxWidth: "300px" }}
                  >
                    <div style={{ lineHeight: "1.6" }}>
                      <strong>{translateText("Prevention Support", "pt")}</strong>
                      <br />• {translateText("Field only available for support employees", "pt")}
                    </div>
                  </Tooltip>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalaryForm;
